import { IconBuildingBank, IconInfoCircle } from '@tabler/icons-react';
import { PaymentDirectACHDto } from '@zorro/clients';
import { Switch } from '@zorro/design-system/v2/Switch';
import { Title } from '@zorro/design-system/v2/Title';
import { Box, Group, Icon, Stack, Text } from '@zorro/web/design-system';
import { getSensitivePaymentData } from '@zorro/web/shared-ui';
import { ChangeEvent } from 'react';

const ACH_DETAILS_FIRST_COLUMN_WIDTH = '11rem';

type Props = {
  directACH: PaymentDirectACHDto;
  withDetails: boolean;
  onToggle: (event: ChangeEvent<HTMLInputElement>) => void;
  badgeText: string;
  withHeader?: boolean;
};

export const DirectACHDetails = ({
  directACH,
  withDetails,
  onToggle,
  badgeText,
  withHeader = false,
}: Props) => {
  const {
    accountNumber,
    routingNumber,
    bankName,
    bankAddress,
    name,
    accountType,
  } = directACH;

  const formattedAccountNumber = getSensitivePaymentData(
    accountNumber,
    !withDetails
  );
  const formattedRoutingNumber = getSensitivePaymentData(
    routingNumber,
    !withDetails
  );

  return (
    <Stack gap="sm">
      {withHeader && (
        <Group gap="xxs">
          <Icon icon={IconBuildingBank} size={20} />
          <Title order={4} size="sm">
            Your payment account
          </Title>
        </Group>
      )}
      <Box
        bg="var(--color-background-system-secondary)"
        p="lg"
        bdrs="md"
        maw="28.75rem"
      >
        <Stack gap="sm">
          <Group gap="md">
            <Stack gap="xxs" w={ACH_DETAILS_FIRST_COLUMN_WIDTH}>
              <Text size="sm" c="var(--color-text-default-secondary)">
                Account number
              </Text>
              <Text size="sm" className="fs-exclude">
                {formattedAccountNumber}
              </Text>
            </Stack>
            <Stack gap="xxs">
              <Text size="sm" c="var(--color-text-default-secondary)">
                Routing number
              </Text>
              <Text size="sm" className="fs-exclude">
                {formattedRoutingNumber}
              </Text>
            </Stack>
          </Group>
          <Group gap="md">
            <Stack gap="xxs" w={ACH_DETAILS_FIRST_COLUMN_WIDTH}>
              <Text size="sm" c="var(--color-text-default-secondary)">
                Bank name
              </Text>
              <Text size="sm">{bankName}</Text>
            </Stack>
            <Stack gap="xxs">
              <Text size="sm" c="var(--color-text-default-secondary)">
                Bank address
              </Text>
              <Text size="sm">{bankAddress}</Text>
            </Stack>
          </Group>
          <Group gap="md">
            <Stack gap="xxs" w={ACH_DETAILS_FIRST_COLUMN_WIDTH}>
              <Text size="sm" c="var(--color-text-default-secondary)">
                Name on account
              </Text>
              <Text size="sm">{name}</Text>
            </Stack>
            <Stack gap="xxs">
              <Text size="sm" c="var(--color-text-default-secondary)">
                Account type
              </Text>
              <Text size="sm">{accountType}</Text>
            </Stack>
          </Group>
        </Stack>
      </Box>
      <Switch
        label="Show account details"
        checked={withDetails}
        onChange={onToggle}
      />
      {badgeText && (
        <Group gap="xs" wrap="nowrap">
          <Icon icon={IconInfoCircle} />
          <Text size="sm">{badgeText}</Text>
        </Group>
      )}
    </Stack>
  );
};
